/**
 * Indicator registry — metadata, default parameters, and display config.
 *
 * displayType:
 *   'overlay' = drawn on the price chart
 *   'panel'   = drawn in a separate oscillator panel below
 */

import * as formulas from './formulas.js';

const INDICATORS = {
  ma: {
    id: 'ma',
    name: 'MA',
    fullName: 'Moving Average',
    displayType: 'overlay',
    params: [
      { key: 'period', label: 'Period', type: 'number', default: 20, min: 1, max: 500 },
      {
        key: 'method', label: 'Method', type: 'select', default: 'sma',
        options: [
          { value: 'sma', label: 'SMA' }, { value: 'ema', label: 'EMA' },
          { value: 'wma', label: 'WMA' }, { value: 'dema', label: 'DEMA' },
          { value: 'tema', label: 'TEMA' }, { value: 'smma', label: 'SMMA (RMA)' },
          { value: 'hma', label: 'HMA' }, { value: 'vwma', label: 'VWMA' },
        ],
      },
      {
        key: 'source', label: 'Source', type: 'select', default: 'close',
        options: [
          { value: 'close', label: 'Close' }, { value: 'open', label: 'Open' },
          { value: 'high', label: 'High' }, { value: 'low', label: 'Low' },
        ],
      },
      { key: 'offset', label: 'Offset', type: 'number', default: 0, min: -500, max: 500 },
    ],
    colors: ['#2962ff'],
    compute: (candles, params) => ({ line: formulas.ma(candles, params) }),
    series: ['line'],
  },
  sma: {
    id: 'sma',
    name: 'SMA',
    fullName: 'Simple Moving Average',
    displayType: 'overlay',
    params: [
      { key: 'period', label: 'Period', type: 'number', default: 20, min: 1, max: 500 },
    ],
    colors: ['#3b82f6'],
    compute: (candles, params) => ({ line: formulas.sma(candles, params) }),
    series: ['line'],
  },
  ema: {
    id: 'ema',
    name: 'EMA',
    fullName: 'Exponential Moving Average',
    displayType: 'overlay',
    params: [
      { key: 'period', label: 'Period', type: 'number', default: 20, min: 1, max: 500 },
    ],
    colors: ['#f59e0b'],
    compute: (candles, params) => ({ line: formulas.ema(candles, params) }),
    series: ['line'],
  },
  wma: {
    id: 'wma',
    name: 'WMA',
    fullName: 'Weighted Moving Average',
    displayType: 'overlay',
    params: [
      { key: 'period', label: 'Period', type: 'number', default: 20, min: 1, max: 500 },
    ],
    colors: ['#8b5cf6'],
    compute: (candles, params) => ({ line: formulas.wma(candles, params) }),
    series: ['line'],
  },
  forceIndex: {
    id: 'forceIndex',
    name: 'Force Index',
    fullName: 'Force Index',
    displayType: 'panel',
    params: [
      { key: 'period', label: 'Period', type: 'number', default: 13, min: 1, max: 200 },
      {
        key: 'method', label: 'Method', type: 'select', default: 'sma',
        options: [
          { value: 'sma', label: 'SMA' }, { value: 'ema', label: 'EMA' },
          { value: 'wma', label: 'WMA' }, { value: 'smma', label: 'SMMA (RMA)' },
        ],
      },
    ],
    colors: ['#22c55e'],
    levels: [0],
    compute: (candles, params) => ({ line: formulas.forceIndex(candles, params) }),
    series: ['line'],
  },
  rsi: {
    id: 'rsi',
    name: 'RSI',
    fullName: 'Relative Strength Index',
    displayType: 'panel',
    params: [
      { key: 'period', label: 'Period', type: 'number', default: 14, min: 1, max: 200 },
    ],
    colors: ['#a855f7'],
    levels: [30, 70],
    compute: (candles, params) => ({ line: formulas.rsi(candles, params) }),
    series: ['line'],
  },
  macd: {
    id: 'macd',
    name: 'MACD',
    fullName: 'Moving Average Convergence Divergence',
    displayType: 'panel',
    params: [
      { key: 'fastPeriod', label: 'Fast', type: 'number', default: 12, min: 1, max: 200 },
      { key: 'slowPeriod', label: 'Slow', type: 'number', default: 26, min: 1, max: 200 },
      { key: 'signalPeriod', label: 'Signal', type: 'number', default: 9, min: 1, max: 200 },
    ],
    colors: ['#3b82f6', '#ef4444'],
    compute: (candles, params) => {
      const r = formulas.macd(candles, params);
      return { macdLine: r.macdLine, signalLine: r.signalLine, histogram: r.histogram };
    },
    series: ['macdLine', 'signalLine', 'histogram'],
  },
  bollingerBands: {
    id: 'bollingerBands',
    name: 'Bollinger Bands',
    fullName: 'Bollinger Bands',
    displayType: 'overlay',
    params: [
      { key: 'period', label: 'Period', type: 'number', default: 20, min: 1, max: 500 },
      { key: 'stdDev', label: 'Std Dev', type: 'number', default: 2, min: 0.1, max: 5, step: 0.1 },
    ],
    colors: ['#6366f1', '#6366f1', '#6366f1'],
    compute: (candles, params) => formulas.bollingerBands(candles, params),
    series: ['upper', 'middle', 'lower'],
  },
  atr: {
    id: 'atr',
    name: 'ATR',
    fullName: 'Average True Range',
    displayType: 'panel',
    params: [
      { key: 'period', label: 'Period', type: 'number', default: 14, min: 1, max: 200 },
    ],
    colors: ['#14b8a6'],
    compute: (candles, params) => ({ line: formulas.atr(candles, params) }),
    series: ['line'],
  },
  stochastic: {
    id: 'stochastic',
    name: 'Stochastic',
    fullName: 'Stochastic Oscillator',
    displayType: 'panel',
    params: [
      { key: 'kPeriod', label: '%K Period', type: 'number', default: 14, min: 1, max: 200 },
      { key: 'dPeriod', label: '%D Period', type: 'number', default: 3, min: 1, max: 50 },
      { key: 'smooth', label: 'Smooth', type: 'number', default: 3, min: 1, max: 50 },
    ],
    colors: ['#3b82f6', '#ef4444'],
    levels: [20, 80],
    compute: (candles, params) => formulas.stochastic(candles, params),
    series: ['k', 'd'],
  },
  adx: {
    id: 'adx',
    name: 'ADX',
    fullName: 'Average Directional Index',
    displayType: 'panel',
    params: [
      { key: 'period', label: 'Period', type: 'number', default: 14, min: 1, max: 200 },
    ],
    colors: ['#f59e0b', '#22c55e', '#ef4444'],
    levels: [25],
    compute: (candles, params) => formulas.adx(candles, params),
    series: ['adx', 'plusDI', 'minusDI'],
  },
  vwap: {
    id: 'vwap',
    name: 'VWAP',
    fullName: 'Volume Weighted Average Price',
    displayType: 'overlay',
    params: [],
    colors: ['#ec4899'],
    compute: (candles) => ({ line: formulas.vwap(candles) }),
    series: ['line'],
  },
  ichimoku: {
    id: 'ichimoku',
    name: 'Ichimoku',
    fullName: 'Ichimoku Cloud',
    displayType: 'overlay',
    params: [
      { key: 'tenkanPeriod', label: 'Tenkan', type: 'number', default: 9, min: 1, max: 200 },
      { key: 'kijunPeriod', label: 'Kijun', type: 'number', default: 26, min: 1, max: 200 },
      { key: 'senkouBPeriod', label: 'Senkou B', type: 'number', default: 52, min: 1, max: 200 },
      { key: 'displacement', label: 'Displacement', type: 'number', default: 26, min: 1, max: 200 },
    ],
    colors: ['#ef4444', '#3b82f6', 'rgba(34,197,94,0.25)', 'rgba(239,68,68,0.25)', '#8b8fa3'],
    compute: (candles, params) => formulas.ichimoku(candles, params),
    series: ['tenkanSen', 'kijunSen', 'cloudUp', 'cloudDown', 'chikouSpan'],
  },
  volume: {
    id: 'volume',
    name: 'Volume',
    fullName: 'Volume Histogram',
    displayType: 'panel',
    params: [],
    colors: [],
    compute: (candles) => ({ histogram: formulas.volumeHistogram(candles) }),
    series: ['histogram'],
  },

  // ─── New Overlays ───────────────────────────────────────────────

  sessions: {
    id: 'sessions',
    name: 'Sessions',
    fullName: 'Trading Sessions (Asia/London/NY)',
    displayType: 'overlay',
    params: [
      { key: 'showLabels', label: 'Show Labels', type: 'number', default: 1, min: 0, max: 1 },
    ],
    colors: [],
    compute: (candles) => ({
      // Pass candles through as-is — the primitive uses raw OHLCV data
      _sessionData: candles.map((c) => ({ time: c.time, value: c.close, _raw: c })),
    }),
    series: ['_sessionData'],
  },
  dema: {
    id: 'dema',
    name: 'DEMA',
    fullName: 'Double Exponential Moving Average',
    displayType: 'overlay',
    params: [
      { key: 'period', label: 'Period', type: 'number', default: 20, min: 1, max: 500 },
    ],
    colors: ['#06b6d4'],
    compute: (candles, params) => ({ line: formulas.dema(candles, params) }),
    series: ['line'],
  },
  tema: {
    id: 'tema',
    name: 'TEMA',
    fullName: 'Triple Exponential Moving Average',
    displayType: 'overlay',
    params: [
      { key: 'period', label: 'Period', type: 'number', default: 20, min: 1, max: 500 },
    ],
    colors: ['#d946ef'],
    compute: (candles, params) => ({ line: formulas.tema(candles, params) }),
    series: ['line'],
  },
  smma: {
    id: 'smma',
    name: 'SMMA',
    fullName: 'Smoothed Moving Average',
    displayType: 'overlay',
    params: [
      { key: 'period', label: 'Period', type: 'number', default: 20, min: 1, max: 500 },
    ],
    colors: ['#84cc16'],
    compute: (candles, params) => ({ line: formulas.smma(candles, params) }),
    series: ['line'],
  },
  supertrend: {
    id: 'supertrend',
    name: 'Supertrend',
    fullName: 'Supertrend',
    displayType: 'overlay',
    params: [
      { key: 'period', label: 'ATR Period', type: 'number', default: 10, min: 1, max: 200 },
      { key: 'multiplier', label: 'Multiplier', type: 'number', default: 3, min: 0.1, max: 10, step: 0.1 },
    ],
    colors: ['#22c55e'],
    compute: (candles, params) => formulas.supertrend(candles, params),
    series: ['line'],
  },
  parabolicSar: {
    id: 'parabolicSar',
    name: 'PSAR',
    fullName: 'Parabolic SAR',
    displayType: 'overlay',
    params: [
      { key: 'step', label: 'Step', type: 'number', default: 0.02, min: 0.001, max: 0.5, step: 0.001 },
      { key: 'max', label: 'Max', type: 'number', default: 0.2, min: 0.01, max: 1, step: 0.01 },
    ],
    colors: ['#f97316'],
    compute: (candles, params) => ({ line: formulas.parabolicSar(candles, params) }),
    series: ['line'],
  },
  keltnerChannel: {
    id: 'keltnerChannel',
    name: 'Keltner',
    fullName: 'Keltner Channel',
    displayType: 'overlay',
    params: [
      { key: 'period', label: 'EMA Period', type: 'number', default: 20, min: 1, max: 500 },
      { key: 'atrPeriod', label: 'ATR Period', type: 'number', default: 10, min: 1, max: 200 },
      { key: 'multiplier', label: 'Multiplier', type: 'number', default: 1.5, min: 0.1, max: 10, step: 0.1 },
    ],
    colors: ['#a855f7', '#a855f7', '#a855f7'],
    compute: (candles, params) => formulas.keltnerChannel(candles, params),
    series: ['upper', 'middle', 'lower'],
  },
  pivotPoints: {
    id: 'pivotPoints',
    name: 'Pivot Points',
    fullName: 'Pivot Points (Classic)',
    displayType: 'overlay',
    params: [],
    colors: ['#f59e0b', '#22c55e', '#22c55e', '#22c55e', '#ef4444', '#ef4444', '#ef4444'],
    compute: (candles) => formulas.pivotPoints(candles),
    series: ['pivot', 'r1', 'r2', 'r3', 's1', 's2', 's3'],
  },
  vwma: {
    id: 'vwma',
    name: 'VWMA',
    fullName: 'Volume Weighted Moving Average',
    displayType: 'overlay',
    params: [
      { key: 'period', label: 'Period', type: 'number', default: 20, min: 1, max: 500 },
    ],
    colors: ['#0ea5e9'],
    compute: (candles, params) => ({ line: formulas.vwma(candles, params) }),
    series: ['line'],
  },

  // ─── New Panels ─────────────────────────────────────────────────

  cci: {
    id: 'cci',
    name: 'CCI',
    fullName: 'Commodity Channel Index',
    displayType: 'panel',
    params: [
      { key: 'period', label: 'Period', type: 'number', default: 20, min: 1, max: 200 },
    ],
    colors: ['#06b6d4'],
    levels: [-100, 100],
    compute: (candles, params) => ({ line: formulas.cci(candles, params) }),
    series: ['line'],
  },
  mfi: {
    id: 'mfi',
    name: 'MFI',
    fullName: 'Money Flow Index',
    displayType: 'panel',
    params: [
      { key: 'period', label: 'Period', type: 'number', default: 14, min: 1, max: 200 },
    ],
    colors: ['#8b5cf6'],
    levels: [20, 80],
    compute: (candles, params) => ({ line: formulas.mfi(candles, params) }),
    series: ['line'],
  },
  obv: {
    id: 'obv',
    name: 'OBV',
    fullName: 'On Balance Volume',
    displayType: 'panel',
    params: [],
    colors: ['#14b8a6'],
    compute: (candles) => ({ line: formulas.obv(candles) }),
    series: ['line'],
  },
  williamsR: {
    id: 'williamsR',
    name: 'Williams %R',
    fullName: 'Williams Percent Range',
    displayType: 'panel',
    params: [
      { key: 'period', label: 'Period', type: 'number', default: 14, min: 1, max: 200 },
    ],
    colors: ['#ec4899'],
    levels: [-20, -80],
    compute: (candles, params) => ({ line: formulas.williamsR(candles, params) }),
    series: ['line'],
  },
  roc: {
    id: 'roc',
    name: 'ROC',
    fullName: 'Rate of Change',
    displayType: 'panel',
    params: [
      { key: 'period', label: 'Period', type: 'number', default: 12, min: 1, max: 200 },
    ],
    colors: ['#f59e0b'],
    levels: [0],
    compute: (candles, params) => ({ line: formulas.roc(candles, params) }),
    series: ['line'],
  },
  momentum: {
    id: 'momentum',
    name: 'Momentum',
    fullName: 'Momentum',
    displayType: 'panel',
    params: [
      { key: 'period', label: 'Period', type: 'number', default: 10, min: 1, max: 200 },
    ],
    colors: ['#3b82f6'],
    levels: [0],
    compute: (candles, params) => ({ line: formulas.momentum(candles, params) }),
    series: ['line'],
  },
  cmf: {
    id: 'cmf',
    name: 'CMF',
    fullName: 'Chaikin Money Flow',
    displayType: 'panel',
    params: [
      { key: 'period', label: 'Period', type: 'number', default: 20, min: 1, max: 200 },
    ],
    colors: ['#84cc16'],
    levels: [0],
    compute: (candles, params) => ({ line: formulas.cmf(candles, params) }),
    series: ['line'],
  },
  stochRsi: {
    id: 'stochRsi',
    name: 'Stoch RSI',
    fullName: 'Stochastic RSI',
    displayType: 'panel',
    params: [
      { key: 'rsiPeriod', label: 'RSI Period', type: 'number', default: 14, min: 1, max: 200 },
      { key: 'stochPeriod', label: 'Stoch Period', type: 'number', default: 14, min: 1, max: 200 },
      { key: 'kSmooth', label: '%K Smooth', type: 'number', default: 3, min: 1, max: 50 },
      { key: 'dSmooth', label: '%D Smooth', type: 'number', default: 3, min: 1, max: 50 },
    ],
    colors: ['#3b82f6', '#ef4444'],
    levels: [20, 80],
    compute: (candles, params) => formulas.stochRsi(candles, params),
    series: ['k', 'd'],
  },
  aroon: {
    id: 'aroon',
    name: 'Aroon',
    fullName: 'Aroon Up/Down',
    displayType: 'panel',
    params: [
      { key: 'period', label: 'Period', type: 'number', default: 25, min: 1, max: 200 },
    ],
    colors: ['#22c55e', '#ef4444'],
    levels: [30, 70],
    compute: (candles, params) => formulas.aroon(candles, params),
    series: ['up', 'down'],
  },
  awesomeOscillator: {
    id: 'awesomeOscillator',
    name: 'AO',
    fullName: 'Awesome Oscillator',
    displayType: 'panel',
    params: [
      { key: 'fastPeriod', label: 'Fast', type: 'number', default: 5, min: 1, max: 100 },
      { key: 'slowPeriod', label: 'Slow', type: 'number', default: 34, min: 1, max: 200 },
    ],
    colors: [],
    levels: [0],
    compute: (candles, params) => ({ histogram: formulas.awesomeOscillator(candles, params) }),
    series: ['histogram'],
  },
  trix: {
    id: 'trix',
    name: 'TRIX',
    fullName: 'Triple Smoothed EMA',
    displayType: 'panel',
    params: [
      { key: 'period', label: 'Period', type: 'number', default: 18, min: 1, max: 200 },
    ],
    colors: ['#d946ef'],
    levels: [0],
    compute: (candles, params) => ({ line: formulas.trix(candles, params) }),
    series: ['line'],
  },
  adLine: {
    id: 'adLine',
    name: 'A/D',
    fullName: 'Accumulation/Distribution',
    displayType: 'panel',
    params: [],
    colors: ['#06b6d4'],
    compute: (candles) => ({ line: formulas.adLine(candles) }),
    series: ['line'],
  },
  chaikinOsc: {
    id: 'chaikinOsc',
    name: 'Chaikin Osc',
    fullName: 'Chaikin Oscillator',
    displayType: 'panel',
    params: [
      { key: 'fastPeriod', label: 'Fast', type: 'number', default: 3, min: 1, max: 50 },
      { key: 'slowPeriod', label: 'Slow', type: 'number', default: 10, min: 1, max: 200 },
    ],
    colors: ['#f97316'],
    levels: [0],
    compute: (candles, params) => ({ line: formulas.chaikinOscillator(candles, params) }),
    series: ['line'],
  },
  donchianChannel: {
    id: 'donchianChannel',
    name: 'Donchian',
    fullName: 'Donchian Channel',
    displayType: 'overlay',
    params: [
      { key: 'period', label: 'Period', type: 'number', default: 20, min: 1, max: 500 },
    ],
    colors: ['#0ea5e9', '#0ea5e9', '#0ea5e9'],
    compute: (candles, params) => formulas.donchianChannel(candles, params),
    series: ['upper', 'middle', 'lower'],
  },
  hma: {
    id: 'hma',
    name: 'HMA',
    fullName: 'Hull Moving Average',
    displayType: 'overlay',
    params: [
      { key: 'period', label: 'Period', type: 'number', default: 20, min: 1, max: 500 },
    ],
    colors: ['#f43f5e'],
    compute: (candles, params) => ({ line: formulas.hma(candles, params) }),
    series: ['line'],
  },
  mcginleyDynamic: {
    id: 'mcginleyDynamic',
    name: 'McGinley',
    fullName: 'McGinley Dynamic',
    displayType: 'overlay',
    params: [
      { key: 'period', label: 'Period', type: 'number', default: 14, min: 1, max: 500 },
    ],
    colors: ['#e879f9'],
    compute: (candles, params) => ({ line: formulas.mcginleyDynamic(candles, params) }),
    series: ['line'],
  },
  zigzag: {
    id: 'zigzag',
    name: 'Zig Zag',
    fullName: 'Zig Zag',
    displayType: 'overlay',
    params: [
      { key: 'deviation', label: 'Deviation %', type: 'number', default: 5, min: 0.1, max: 50, step: 0.1 },
    ],
    colors: ['#f59e0b'],
    compute: (candles, params) => ({ line: formulas.zigzag(candles, params) }),
    series: ['line'],
  },
  linearRegression: {
    id: 'linearRegression',
    name: 'Lin Reg',
    fullName: 'Linear Regression Channel',
    displayType: 'overlay',
    params: [
      { key: 'period', label: 'Period', type: 'number', default: 100, min: 5, max: 500 },
      { key: 'deviations', label: 'Deviations', type: 'number', default: 2, min: 0.5, max: 5, step: 0.1 },
    ],
    colors: ['#6366f1', '#6366f1', '#6366f1'],
    compute: (candles, params) => formulas.linearRegressionChannel(candles, params),
    series: ['upper', 'middle', 'lower'],
  },
  adxr: {
    id: 'adxr',
    name: 'ADXR',
    fullName: 'Average Directional Movement Rating',
    displayType: 'panel',
    params: [
      { key: 'period', label: 'Period', type: 'number', default: 14, min: 1, max: 200 },
    ],
    colors: ['#f59e0b', '#22c55e', '#ef4444'],
    levels: [25],
    compute: (candles, params) => formulas.adxr(candles, params),
    series: ['adxr', 'plusDI', 'minusDI'],
  },
  kst: {
    id: 'kst',
    name: 'KST',
    fullName: 'Know Sure Thing',
    displayType: 'panel',
    params: [
      { key: 'roc1', label: 'ROC1', type: 'number', default: 10, min: 1, max: 100 },
      { key: 'roc2', label: 'ROC2', type: 'number', default: 15, min: 1, max: 100 },
      { key: 'roc3', label: 'ROC3', type: 'number', default: 20, min: 1, max: 100 },
      { key: 'roc4', label: 'ROC4', type: 'number', default: 30, min: 1, max: 100 },
      { key: 'signalPeriod', label: 'Signal', type: 'number', default: 9, min: 1, max: 50 },
    ],
    colors: ['#3b82f6', '#ef4444'],
    levels: [0],
    compute: (candles, params) => formulas.kst(candles, params),
    series: ['kst', 'signal'],
  },
  stdDev: {
    id: 'stdDev',
    name: 'Std Dev',
    fullName: 'Standard Deviation',
    displayType: 'panel',
    params: [
      { key: 'period', label: 'Period', type: 'number', default: 20, min: 1, max: 500 },
    ],
    colors: ['#8b5cf6'],
    compute: (candles, params) => ({ line: formulas.stdDev(candles, params) }),
    series: ['line'],
  },
  elderRay: {
    id: 'elderRay',
    name: 'Elder-Ray',
    fullName: 'Elder-Ray Bull/Bear Power',
    displayType: 'panel',
    params: [
      { key: 'period', label: 'EMA Period', type: 'number', default: 13, min: 1, max: 200 },
    ],
    colors: ['#22c55e', '#ef4444'],
    levels: [0],
    compute: (candles, params) => formulas.elderRay(candles, params),
    series: ['bull', 'bear'],
  },
  coppockCurve: {
    id: 'coppockCurve',
    name: 'Coppock',
    fullName: 'Coppock Curve',
    displayType: 'panel',
    params: [
      { key: 'longRoc', label: 'Long ROC', type: 'number', default: 14, min: 1, max: 100 },
      { key: 'shortRoc', label: 'Short ROC', type: 'number', default: 11, min: 1, max: 100 },
      { key: 'wmaPeriod', label: 'WMA Period', type: 'number', default: 10, min: 1, max: 100 },
    ],
    colors: ['#06b6d4'],
    levels: [0],
    compute: (candles, params) => ({ line: formulas.coppockCurve(candles, params) }),
    series: ['line'],
  },
  custom: {
    id: 'custom',
    name: 'Custom Formula',
    fullName: 'Custom Formula Indicator',
    displayType: 'panel',
    params: [
      { key: 'formula', label: 'Formula', type: 'text', default: 'SMA(close,20) - SMA(close,50)' },
    ],
    colors: ['#f59e0b'],
    compute: (candles, params) => {
      try {
        const result = evaluateFormula(candles, params.formula || '');
        return { line: result };
      } catch (e) {
        console.warn('Custom formula error:', e.message);
        return { line: candles.map(c => ({ time: c.time, value: 0 })) };
      }
    },
    series: ['line'],
  },
};

/**
 * Simple formula evaluator for custom indicators.
 * Supports: SMA(source,period), EMA(source,period), close, open, high, low, volume
 * Operators: +, -, *, /
 * Sources: close, open, high, low, volume, hl2, hlc3, ohlc4
 */
function evaluateFormula(candles, formula) {
  const getSource = (name) => {
    switch (name.trim()) {
      case 'close': return candles.map(c => c.close);
      case 'open': return candles.map(c => c.open);
      case 'high': return candles.map(c => c.high);
      case 'low': return candles.map(c => c.low);
      case 'volume': return candles.map(c => c.volume);
      case 'hl2': return candles.map(c => (c.high + c.low) / 2);
      case 'hlc3': return candles.map(c => (c.high + c.low + c.close) / 3);
      case 'ohlc4': return candles.map(c => (c.open + c.high + c.low + c.close) / 4);
      default: return null;
    }
  };

  const computeSMA = (values, period) => {
    const result = new Array(values.length).fill(null);
    for (let i = period - 1; i < values.length; i++) {
      let sum = 0;
      for (let j = 0; j < period; j++) sum += values[i - j];
      result[i] = sum / period;
    }
    return result;
  };

  const computeEMA = (values, period) => {
    const result = new Array(values.length).fill(null);
    const k = 2 / (period + 1);
    let ema = values.slice(0, period).reduce((a, b) => a + b, 0) / period;
    for (let i = 0; i < values.length; i++) {
      if (i < period - 1) { result[i] = null; continue; }
      if (i === period - 1) { result[i] = ema; continue; }
      ema = values[i] * k + ema * (1 - k);
      result[i] = ema;
    }
    return result;
  };

  // Parse and resolve function calls: SMA(close,20), EMA(high,14), RSI(close,14)
  let resolved = formula;
  const funcRegex = /(SMA|EMA|WMA|RSI)\((\w+),\s*(\d+)\)/gi;
  const resolvedArrays = {};
  let matchIdx = 0;

  resolved = resolved.replace(funcRegex, (match, func, src, periodStr) => {
    const key = `__arr${matchIdx++}__`;
    const source = getSource(src);
    if (!source) throw new Error(`Unknown source: ${src}`);
    const period = parseInt(periodStr);
    let arr;
    switch (func.toUpperCase()) {
      case 'SMA': arr = computeSMA(source, period); break;
      case 'EMA': arr = computeEMA(source, period); break;
      default: arr = computeSMA(source, period);
    }
    resolvedArrays[key] = arr;
    return key;
  });

  // Also resolve plain sources
  ['close', 'open', 'high', 'low', 'volume', 'hl2', 'hlc3', 'ohlc4'].forEach(src => {
    if (resolved.includes(src) && !resolved.includes(`__arr`)) {
      const key = `__arr${matchIdx++}__`;
      resolvedArrays[key] = getSource(src);
      resolved = resolved.replace(new RegExp(`\\b${src}\\b`, 'g'), key);
    }
  });

  // Evaluate expression element-wise
  const keys = Object.keys(resolvedArrays);
  if (keys.length === 0) throw new Error('No valid expressions');

  const len = candles.length;
  const result = [];
  for (let i = 0; i < len; i++) {
    let expr = resolved;
    for (const k of keys) {
      const val = resolvedArrays[k][i];
      expr = expr.replace(new RegExp(k.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'g'), val == null ? 'null' : String(val));
    }
    try {
      if (expr.includes('null')) { result.push({ time: candles[i].time, value: 0 }); continue; }
      // Safe eval: only numbers and operators
      const sanitized = expr.replace(/[^0-9+\-*/().e\s]/g, '');
      const val = Function(`"use strict"; return (${sanitized})`)();
      result.push({ time: candles[i].time, value: isFinite(val) ? Math.round(val * 1e6) / 1e6 : 0 });
    } catch {
      result.push({ time: candles[i].time, value: 0 });
    }
  }
  return result;
}

export function getIndicator(id) {
  return INDICATORS[id] || null;
}

export function getAllIndicators() {
  return Object.values(INDICATORS);
}

export function getOverlayIndicators() {
  return Object.values(INDICATORS).filter((i) => i.displayType === 'overlay');
}

export function getPanelIndicators() {
  return Object.values(INDICATORS).filter((i) => i.displayType === 'panel');
}

export default INDICATORS;
